import {
  CircularProgress,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { Clear, Done, Help, Warning } from "@material-ui/icons";
import { ClassNameMap } from "@material-ui/styles";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  FormattedSensorValue,
  MaintenanceDashboardApiFactory,
  VehicleSensorAttributeValueDto,
  VehicleSensorAttributeValueType,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import isNullOrUndefined from "../../../../common/util/isNullOrUndefined";
import { Empty } from "../widgets/Empty";
import { Widget } from "../widgets/Widget";
import { VehicleSensorInfoWidgetEditor } from "./VehicleSensorInfoWidgetEditor";
import { VehicleWidgetPropsWithConfiguration } from "./VehicleWidgetProps";

type VehicleSensorAttributes = {
  [key: string]: VehicleSensorAttributeValueDto;
};
const useStyles = makeStyles((theme: Theme) => ({
  warning: {
    color: theme.palette.warning.main,
  },
  editButton: {
    color: theme.palette.text.primary,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  border: {
    borderColor: theme.palette.action.disabled,
  },
}));

const getFormattedValue = (
  formattedValue: FormattedSensorValue | undefined | null,
  classes: ClassNameMap<"warning" | "border" | "editButton">
) => {
  if (!formattedValue) {
    return (
      <div className={`info-value-icon ${classes.warning}`}>
        <Help />
      </div>
    );
  }

  if (isNullOrUndefined(formattedValue.value)) {
    return (
      <div className={`info-value-icon ${classes.warning}`}>
        <Warning />
      </div>
    );
  }

  if (formattedValue.valueType === VehicleSensorAttributeValueType.Boolean) {
    return (
      <div className={`info-value-icon`}>
        {formattedValue.value ? <Done /> : <Clear />}
      </div>
    );
  }

  if (formattedValue.valueType === VehicleSensorAttributeValueType.Decimal) {
    return (
      <>
        <Typography variant="body1">
          {(formattedValue.value as Number).toFixed(2)} {formattedValue!.unit}
        </Typography>
        {!formattedValue.isNormal ? (
          <div className={`info-value-icon ${classes.warning}`}>
            <Warning />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Typography variant="body1">
        {formattedValue.value} {formattedValue!.unit}
      </Typography>
      {!formattedValue.isNormal ? (
        <div className={`info-value-icon ${classes.warning}`}>
          <Warning />
        </div>
      ) : null}
    </>
  );
};

export const VehicleSensorInfoWidget = ({
  vehicle,
  configuration,
  onConfigurationSaved,
  ...props
}: VehicleWidgetPropsWithConfiguration<string[]>) => {
  const classes = useStyles();
  const translate = useTranslate();
  const axios = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<VehicleSensorAttributes>();
  const [editing, setEditing] = useState<boolean>(false);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>(
    configuration ?? []
  );

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!axios) {
      setLoading(false);
      return;
    }

    if (!vehicle?.eldDeviceId) {
      setLoading(false);
      return;
    }

    if (!vehicle.id || isNaN(vehicle.id)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMaintenancedashboardVehiclesensorattributevaluesGet(
        vehicle.id,
        selectedAttributes
      )
      .then((result) => {
        setData(result.data);
      })
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [axios, vehicle, selectedAttributes]);

  const handleChange = useCallback(
    (value?: string[]) => {
      value && setSelectedAttributes(value);
      value && onConfigurationSaved && onConfigurationSaved(value);
      setEditing(false);
    },
    [onConfigurationSaved]
  );

  const displayData =
    data &&
    Object.getOwnPropertyNames(data).map((x) => ({ label: x, value: data[x] }));

  const widgetProps = {
    ...props,
    title: props.title,
    // editIcon: !editing ? (
    //   <Edit className={classes.editButton} onClick={() => setEditing(true)} />
    // ) : null,
  };
  return (
    <Widget {...widgetProps} ref={ref}>
      <div className="categories">
        {loading ? (
          <div className="category">
            <CircularProgress size="3rem" />
          </div>
        ) : !editing ? (
          displayData && displayData.length ? (
            <div className="info-wrapper info-wrapper-3-1">
              {displayData!.map(({ label, value: { formattedValue } }) => (
                <Fragment key={label}>
                  <div
                    className={`info-label info-label-outlined ${classes.border}`}
                  >
                    <Typography variant="body2" component="div">
                      {translate(`resources.sensorAttributes.${label}`, {
                        _: label,
                      })}
                    </Typography>
                  </div>
                  <div className="info-value">
                    {getFormattedValue(formattedValue, classes)}
                  </div>
                </Fragment>
              ))}
            </div>
          ) : (
            <Empty />
          )
        ) : (
          <VehicleSensorInfoWidgetEditor
            selectedAttributes={selectedAttributes}
            onChange={handleChange}
            widgetHeight={ref.current?.clientHeight}
          />
        )}
      </div>
    </Widget>
  );
};
